import React, { useEffect } from "react";
import { useLocalStore } from "easy-peasy";
import { Box, Link, Paper, TableRow, TableCell } from "@mui/material";
import Spinner from "components/admin_v2/ui/Spinner";
import PaginatedTable from "components/admin_v2/ui/PaginatedTable";
import BatchSelect from "components/admin_v2/common/BatchSelect";
import TopPageLayout from "components/admin_v2/nav/TopPageLayout";
import SettingsConfigTabs from "components/admin_v2/settings_config/SettingsConfigTabs";
import AddProgramDialog from "./AddProgramDialog";
import RouterLink from "components/admin_v2/nav/RouterLink";
import ProgramsFilter from "components/admin_v2/settings_config/programs/ProgramsFilter";
import SearchInput from "components/admin_v2/ui/SearchInput";
import GreenCheckbox from "components/admin_v2/ui/GreenCheckbox";
import { OutlinedBtn } from "components/admin_v2/ui/Buttons";
import { programsStore } from "components/admin_v2/settings_config/stores/programsStore";
import { updateWithRouter, camelizeLocationSearch } from "lib/queryString";
import { pageRoutes } from "services/api";
import { batchArchive, batchUnarchive } from "services/apiPrograms";
import useSorting from "lib/useSorting";
import I18n from "utils/i18n.js";
import useDebounce from "lib/useDebounce";
import useCommonPageStyles from "components/admin_v2/common/useCommonPageStyles";

const Programs = (props) => {
  const cls = useCommonPageStyles();
  const store = useLocalStore(() => programsStore(camelizeLocationSearch(props)));
  const [state, actions] = store;
  const searchInput = useDebounce(state.query);
  const updateQueryString = updateWithRouter(props);
  const { sortBy, sortableColumn } = useSorting({ field: "created_at", order: "desc" });

  useEffect(() => {
    actions.setFromRouter(camelizeLocationSearch(props));
  }, [props.location.search]);

  useEffect(() => {
    actions.fetchPrograms({ sortByField: sortBy.field, sortByOrder: sortBy.order });
  }, [searchInput, state.page, state.perPage, state.scope, state.refresh, sortBy]);

  useEffect(() => {
    updateQueryString({ page: 1, query: searchInput });
  }, [searchInput]);

  let actionsList = [];
  if (state.scope == "archived") {
    actionsList.push({ label: I18n.t("ui.btn.unarchive"), action: batchUnarchive });
  } else {
    actionsList.push({ label: I18n.t("ui.btn.archive"), action: batchArchive });
  }

  const updatePage = (_e, newPage) => updateQueryString({ page: newPage + 1 });

  const updatePerPage = (e) => {
    updateQueryString({ page: 1, per_page: e.target.value });
  };

  const onSearch = (e) => {
    actions.setQuery(e.target.value);
  };

  const toggleAddProgramDialog = () => {
    actions.setAddProgramDialogOpen(!state.addProgramDialogOpen);
  };

  const headers = [
    {
      field: "batchCheck",
      label: (
        <GreenCheckbox
          checked={state.allSelected}
          onChange={(e) => actions.updateAllSelected(e.target.checked)}
        />
      ),
      skipSorting: true
    },
    { field: "id", label: "ID" },
    { field: "name", label: "Name" },
    { field: "abbreviation", label: "Abbreviation" },
    { field: "created_at", label: "Created" },
    { field: "", label: "" }
  ].map(sortableColumn);

  const row = (program) => (
    <TableRow key={`program-${program.id}`}>
      <TableCell component="th" style={{ width: "5%" }}>
        <GreenCheckbox
          checked={state.isSelected(program.id)}
          onChange={(e) => actions.updateSelectedIds({ isAdd: e.target.checked, id: program.id })}
        />
      </TableCell>
      <TableCell component="th" style={{ width: "5%" }}>
        <RouterLink to={pageRoutes.program(program.id)} className={cls.tableLink}>
          {program.id}
        </RouterLink>
      </TableCell>
      <TableCell style={{ width: "25%" }}>{program.name}</TableCell>
      <TableCell style={{ width: "25%" }}>{program.abbreviation}</TableCell>
      <TableCell>{program.createdAt}</TableCell>
      <TableCell>
        <RouterLink to={pageRoutes.program(program.id)} className={cls.tableLink}>
          {I18n.t("ui.btn.view")}
        </RouterLink>
        <RouterLink to={pageRoutes.programEdit(program.id)} className={cls.tableLink}>
          {I18n.t("ui.btn.edit")}
        </RouterLink>
        <Link
          onClick={() => actions.toggleArchive(program.id)}
          className={cls.tableLink}
          underline="hover"
        >
          {program.discarded ? I18n.t("ui.btn.unarchive") : I18n.t("ui.btn.archive")}
        </Link>
      </TableCell>
    </TableRow>
  );

  return (
    <React.Fragment>
      <TopPageLayout title={I18n.t("settings_config.title")} />
      <SettingsConfigTabs />
      <Box className={cls.btnsWrapper}>
        <OutlinedBtn onClick={toggleAddProgramDialog}>
          {I18n.t("settings_config.new_program.btn")}
        </OutlinedBtn>
      </Box>
      <Paper className={cls.paper}>
        <Box className={cls.filters}>
          <BatchSelect store={store} actionsList={actionsList} />
          <ProgramsFilter state={state} updateQueryString={updateQueryString} />
          <SearchInput cls value={state.query} onChange={onSearch} />
        </Box>
        {state.loading ? (
          <Spinner />
        ) : (
          <PaginatedTable
            headers={headers}
            count={state.total}
            perPage={state.perPage}
            page={state.page}
            onPageChange={updatePage}
            onChangePerPage={updatePerPage}
          >
            {state.programs.map(row)}
          </PaginatedTable>
        )}
      </Paper>
      <AddProgramDialog
        cls={cls}
        open={state.addProgramDialogOpen}
        store={store}
        toggleDialog={toggleAddProgramDialog}
      />
    </React.Fragment>
  );
};

export default Programs;
